import React, { memo, useMemo } from "react";
import { YAxis } from "@chartLib/lib/axes";
import debug from "debug";
import PropTypes from "prop-types";
import {
  formatPrice,
  normalizePrice,
  getTickSize,
} from "../../../utils/priceUtils.js";
import {
  formatNumber,
  formatVolumeAbbrev,
} from "../../../utils/numberFormatting.js";

const log = debug("odin:YAxisBase");

const NICE_MULTIPLIERS = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];

export function computePriceTickValues(domain, symbol, targetTicks = 7) {
  const [min, max] = domain;
  if (!isFinite(min) || !isFinite(max) || max <= min) return undefined;

  const tickSize = getTickSize(symbol);
  if (!tickSize || tickSize <= 0) return undefined;

  const tickSizeStr = tickSize.toString();
  const dotIndex = tickSizeStr.indexOf(".");
  const decimalPlaces = dotIndex === -1 ? 0 : tickSizeStr.length - dotIndex - 1;

  const rawInterval = (max - min) / targetTicks;

  let interval = tickSize * NICE_MULTIPLIERS[NICE_MULTIPLIERS.length - 1];
  for (const m of NICE_MULTIPLIERS) {
    if (tickSize * m >= rawInterval) {
      interval = tickSize * m;
      break;
    }
  }

  const start = parseFloat(
    (Math.ceil(min / interval) * interval).toFixed(decimalPlaces),
  );
  const epsilon = tickSize * 0.001;
  const ticks = [];
  let v = start;
  while (v <= max + epsilon) {
    ticks.push(parseFloat(v.toFixed(decimalPlaces)));
    v = parseFloat((v + interval).toFixed(decimalPlaces));
  }

  if (ticks.length < 2 || ticks.length > targetTicks * 3) return undefined;
  return ticks;
}

const LABEL_SPACING_PX = 40; // minimum pixels between Y-axis labels

const YAxisBase = memo(
  ({
    themeColors,
    ticks = 5,
    inverted = false,
    showGrid,
    gridWidth,
    chartSymbol,
    isPriceAxis = true,
    decimalPlaces,
    isVolumeAxis = false,
    isCompareMode = false,
    mainBaseline = null, // Baseline price for percentage calculation in compare mode
    height,
  }) => {
    const targetTicks = height
      ? Math.max(3, Math.floor(height / LABEL_SPACING_PX))
      : ticks;

    // Create tick format function for price-based Y-axes
    const tickFormat = useMemo(() => {
      // Compare mode: convert price to percentage using baseline
      if (isCompareMode && mainBaseline) {
        return (val) => {
          // val is a price, convert to percentage relative to baseline
          const percent = ((val - mainBaseline) / mainBaseline) * 100;
          const sign = percent >= 0 ? "+" : "";
          return `${sign}${percent.toFixed(2)}%`;
        };
      }

      // Compare mode without baseline (fallback - shouldn't normally happen)
      if (isCompareMode) {
        return (val) => {
          const sign = val >= 0 ? "+" : "";
          return `${sign}${val.toFixed(2)}%`;
        };
      }

      // Volume axis: use K/M/B suffixes (supports negative values for Force Index etc.)
      if (isVolumeAxis) {
        return (val) => formatVolumeAbbrev(val);
      }

      // Indicator axis (non-price): use formatNumber with locale-aware formatting
      if (!isPriceAxis) {
        if (typeof decimalPlaces === "number") {
          return (v) => formatNumber(v ?? 0, decimalPlaces);
        }
        // fallback reasonable precision for indicators
        return (v) =>
          Math.abs(v) >= 1 ? formatNumber(v ?? 0, 2) : formatNumber(v ?? 0, 4);
      }

      // Price axis with symbol normalization
      if (!chartSymbol) return undefined;
      return (value) => {
        try {
          const adjustedValue = normalizePrice(chartSymbol, value);
          return formatPrice(chartSymbol, adjustedValue);
        } catch (error) {
          log("Error formatting tick value:", error);
          return Number(value ?? 0).toFixed(2); // fallback
        }
      };
    }, [
      chartSymbol,
      isPriceAxis,
      decimalPlaces,
      isVolumeAxis,
      isCompareMode,
      mainBaseline,
    ]);

    const priceTickValues = useMemo(() => {
      if (!isPriceAxis || isCompareMode || !chartSymbol) return undefined;
      return (domain) =>
        computePriceTickValues(domain, chartSymbol, targetTicks);
    }, [isPriceAxis, isCompareMode, chartSymbol, targetTicks]);

    if (showGrid) {
      // When grid is enabled, render single axis with grid lines
      return (
        <YAxis
          axisAt="right"
          orient="right"
          ticks={targetTicks}
          tickValues={priceTickValues}
          inverted={inverted}
          // stroke="#FFFFFF"
          // tickLabelFill="#FFFFFF"
          // tickStroke="#FFFFFF"
          // tickStrokeOpacity={1}

          stroke={themeColors.basicColor}
          tickLabelFill={themeColors.basicColor}
          tickStroke={themeColors.gridColor}
          tickStrokeOpacity={1}
          edgeClip={false}
          innerTickSize={-1 * gridWidth}
          tickStrokeDasharray="2, 6"
          showTickLabel={true}
          tickFormat={tickFormat}
          showDomain={true}
          onDoubleClick={(...rest) => log("yAxis - double click %o", rest)}
          onContextMenu={(...rest) => log("yAxis - context menu %o", rest)}
        />
      );
    }

    return (
      <YAxis
        axisAt="right"
        orient="right"
        ticks={ticks}
        tickValues={priceTickValues}
        inverted={inverted}
        stroke={themeColors.basicColor}
        edgeClip={false}
        innerTickSize={5}
        tickStroke={themeColors.basicColor}
        tickFormat={tickFormat}
        onDoubleClick={(...rest) => log("yAxis - double click %o", rest)}
        onContextMenu={(...rest) => log("yAxis - context menu %o", rest)}
      />
    );
  },
);

YAxisBase.propTypes = {
  themeColors: PropTypes.object.isRequired,
  ticks: PropTypes.number,
  inverted: PropTypes.bool,
  showGrid: PropTypes.bool,
  gridWidth: PropTypes.number,
  chartSymbol: PropTypes.string,
  isPriceAxis: PropTypes.bool,
  decimalPlaces: PropTypes.number,
  isVolumeAxis: PropTypes.bool,
  isCompareMode: PropTypes.bool,
  mainBaseline: PropTypes.number, // Baseline price for compare mode percentage display
  height: PropTypes.number,
};

YAxisBase.displayName = "YAxisBase";

export default YAxisBase;
